import { supabase } from './supabase'

interface RuleLogic {
  keywords?: string[]
  patterns?: string[]
  negations?: string[]
}

interface ClassificationResult {
  category_id: string | null
  category_name: string | null
  subcategory_id: string | null
  subcategory_name: string | null
  matched_ruleset: string | null
  confidence: 'high' | 'medium' | 'low' | 'none'
  tags: {
    tag_id: string
    tag_name: string
    matched_ruleset: string
    confidence: number
  }[]
  debug: {
    ruleset: string
    priority: number
    status: 'matched' | 'failed' | 'skipped'
    reason: string
    details?: any
  }[]
}

/**
 * Classifies an item based on subcategory rules with two-tier strategy
 * Tier 1: Try title + URL only (prioritizes product name)
 * Tier 2: Fallback to title + description + URL (catches edge cases)
 * First matching subcategory wins and determines the category
 */
export async function classifyItemBySubcategory(
  title: string,
  description: string,
  sourceUrl: string
): Promise<ClassificationResult> {
  const debug: ClassificationResult['debug'] = []

  try {
    // Load all active subcategory rulesets ordered by priority
    const { data: rulesets, error: rulesetsError } = await supabase
      .from('rulesets')
      .select('id, scope, target_id, version, status, display_order, description, created_at, updated_at, published_at, published_by')
      .eq('scope', 'subcategory')
      .eq('status', 'active')
      .order('display_order', { ascending: true })

    if (rulesetsError) {
      console.error('❌ Error loading rulesets:', rulesetsError)
      throw rulesetsError
    }

    if (!rulesets || rulesets.length === 0) {
      return await getOtherCategoryResult(debug)
    }

    // Load subcategories to get names and parent categories
    const { data: subcategories, error: subcategoriesError } = await supabase
      .from('subcategories')
      .select('id, name, category_id, categories(id, name)')
      .eq('active', true)

    if (subcategoriesError) throw subcategoriesError

    // TIER 1: Try title + URL only (prioritizes product name)
    const titleText = `${title} ${sourceUrl}`.toLowerCase()

    debug.push({
      ruleset: '--- TIER 1: Title-based Classification ---',
      priority: 0,
      status: 'skipped',
      reason: 'Trying classification based on title only for higher accuracy'
    })

    for (const ruleset of rulesets) {
      const subcategory = subcategories?.find(s => s.id === ruleset.target_id)
      if (!subcategory) continue

      let rules: RuleLogic
      try {
        rules = ruleset.description ? JSON.parse(ruleset.description) : {}
      } catch (parseError) {
        console.warn(`⚠️  Failed to parse rules for ${subcategory.name}:`, parseError)
        continue
      }

      const result = evaluateRules(titleText, rules, subcategory.name)

      if (result.matched) {
        debug.push({
          ruleset: `${subcategory.name} (title match)`,
          priority: ruleset.display_order,
          status: 'matched',
          reason: `Tier 1: ${result.reason}`,
          details: result.details
        })

        const categoryName = (subcategory.categories as any)?.name || null
        return {
          category_id: subcategory.category_id,
          category_name: categoryName,
          subcategory_id: subcategory.id,
          subcategory_name: subcategory.name,
          matched_ruleset: `${subcategory.name} v${ruleset.version} (title)`,
          confidence: 'high',
          tags: [],
          debug
        }
      }
    }

    // TIER 2: Fallback to full text (title + description + URL)
    const fullText = `${title} ${description} ${sourceUrl}`.toLowerCase()

    debug.push({
      ruleset: '--- TIER 2: Full Text Classification ---',
      priority: 0,
      status: 'skipped',
      reason: 'No title match found, trying with description included'
    })

    for (const ruleset of rulesets) {
      const subcategory = subcategories?.find(s => s.id === ruleset.target_id)

      if (!subcategory) {
        debug.push({
          ruleset: `Unknown Subcategory (${ruleset.target_id})`,
          priority: ruleset.display_order,
          status: 'skipped',
          reason: 'Subcategory not found in database'
        })
        continue
      }

      let rules: RuleLogic
      try {
        rules = ruleset.description ? JSON.parse(ruleset.description) : {}
      } catch (parseError) {
        console.warn(`⚠️  Failed to parse rules for ${subcategory.name}:`, parseError)
        debug.push({
          ruleset: subcategory.name,
          priority: ruleset.display_order,
          status: 'failed',
          reason: 'Invalid JSON in ruleset description',
          details: { error: parseError }
        })
        continue
      }

      const result = evaluateRules(fullText, rules, subcategory.name)

      debug.push({
        ruleset: subcategory.name,
        priority: ruleset.display_order,
        status: result.matched ? 'matched' : 'failed',
        reason: `Tier 2: ${result.reason}`,
        details: result.details
      })

      if (result.matched) {
        const categoryName = (subcategory.categories as any)?.name || null

        return {
          category_id: subcategory.category_id,
          category_name: categoryName,
          subcategory_id: subcategory.id,
          subcategory_name: subcategory.name,
          matched_ruleset: `${subcategory.name} v${ruleset.version}`,
          confidence: 'medium', // Lower confidence since it matched on description
          tags: [],
          debug
        }
      }
    }

    // No matches found - return "Other" category
    return await getOtherCategoryResult(debug)

  } catch (error) {
    console.error('❌ Classification error:', error)
    throw error
  }
}

/**
 * Checks if a keyword exists as a complete word in the text (not as a substring)
 * Uses word boundary matching to prevent "food" from matching "footwear"
 */
function matchesAsWord(text: string, keyword: string): boolean {
  // Escape special regex characters in the keyword
  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  // Create regex with word boundaries
  const wordBoundaryRegex = new RegExp(`\\b${escapedKeyword}\\b`, 'i')
  return wordBoundaryRegex.test(text)
}

/**
 * Evaluates rule logic against item text
 */
function evaluateRules(
  itemText: string,
  rules: RuleLogic,
  rulesetName: string
): { matched: boolean; reason: string; details: any } {
  const details: any = {
    keywords: { checked: [], matched: [] },
    patterns: { checked: [], matched: [] },
    negations: { checked: [], matched: [] }
  }

  // Check negations first (if any negation matches, rule fails)
  if (rules.negations && rules.negations.length > 0) {
    for (const negation of rules.negations) {
      details.negations.checked.push(negation)
      if (matchesAsWord(itemText, negation.toLowerCase())) {
        details.negations.matched.push(negation)
        return {
          matched: false,
          reason: `Negation matched: "${negation}"`,
          details
        }
      }
    }
  }

  // Check keywords (any match = success)
  if (rules.keywords && rules.keywords.length > 0) {
    for (const keyword of rules.keywords) {
      details.keywords.checked.push(keyword)
      if (matchesAsWord(itemText, keyword.toLowerCase())) {
        details.keywords.matched.push(keyword)
        return {
          matched: true,
          reason: `Keyword matched: "${keyword}"`,
          details
        }
      }
    }
  }

  // Check regex patterns (any match = success)
  if (rules.patterns && rules.patterns.length > 0) {
    for (const pattern of rules.patterns) {
      details.patterns.checked.push(pattern)
      try {
        const regex = new RegExp(pattern, 'i')
        if (regex.test(itemText)) {
          details.patterns.matched.push(pattern)
          return {
            matched: true,
            reason: `Pattern matched: ${pattern}`,
            details
          }
        }
      } catch (regexError) {
        details.patterns.matched.push(`ERROR: ${pattern}`)
      }
    }
  }

  // No rules defined or nothing matched
  if (!rules.keywords?.length && !rules.patterns?.length) {
    return {
      matched: false,
      reason: 'No keywords or patterns defined in ruleset',
      details
    }
  }

  return {
    matched: false,
    reason: 'No keywords or patterns matched',
    details
  }
}

/**
 * Returns the "Other" category as default
 */
async function getOtherCategoryResult(
  debug: ClassificationResult['debug']
): Promise<ClassificationResult> {
  // Find "Other" category
  const { data: otherCategory } = await supabase
    .from('categories')
    .select('id, name')
    .eq('name', 'Other')
    .single()

  if (!otherCategory) {
    throw new Error('Other category not found in database')
  }

  return {
    category_id: otherCategory.id,
    category_name: otherCategory.name,
    subcategory_id: null,
    subcategory_name: null,
    matched_ruleset: null,
    confidence: 'none',
    tags: [],
    debug
  }
}

/**
 * Classifies an item based on category rules (fallback when no subcategory matches)
 * First matching category wins
 */
async function classifyItemByCategory(
  itemText: string,
  debug: ClassificationResult['debug']
): Promise<{ category_id: string | null; category_name: string | null; matched_ruleset: string | null } | null> {
  try {
    // Load all active category rulesets ordered by priority
    const { data: rulesets, error: rulesetsError } = await supabase
      .from('rulesets')
      .select('id, scope, target_id, version, status, display_order, description, created_at, updated_at, published_at, published_by')
      .eq('scope', 'category')
      .eq('status', 'active')
      .order('display_order', { ascending: true })

    if (rulesetsError) throw rulesetsError
    if (!rulesets || rulesets.length === 0) return null

    // Load categories to get names
    const { data: categories, error: categoriesError } = await supabase
      .from('categories')
      .select('id, name')
      .eq('active', true)

    if (categoriesError) throw categoriesError

    // Evaluate each ruleset in priority order
    for (const ruleset of rulesets) {
      const category = categories?.find(c => c.id === ruleset.target_id)
      if (!category) continue

      let rules: RuleLogic
      try {
        rules = ruleset.description ? JSON.parse(ruleset.description) : {}
      } catch (parseError) {
        console.warn(`⚠️  Failed to parse rules for ${category.name}:`, parseError)
        continue
      }

      const result = evaluateRules(itemText, rules, category.name)

      debug.push({
        ruleset: category.name,
        priority: ruleset.display_order,
        status: result.matched ? 'matched' : 'failed',
        reason: result.reason,
        details: result.details
      })

      if (result.matched) {
        return {
          category_id: category.id,
          category_name: category.name,
          matched_ruleset: `${category.name} v${ruleset.version}`
        }
      }
    }

    return null
  } catch (error) {
    console.error('❌ Category classification error:', error)
    return null
  }
}

/**
 * Classifies an item by tags with two-tier strategy
 * Tier 1: Prioritize matches in title (higher confidence)
 * Tier 2: Matches in description (lower confidence)
 * ALL matching tag rulesets apply (not first-match-wins)
 */
export async function classifyItemByTags(
  title: string,
  description: string,
  sourceUrl: string
): Promise<{ tag_id: string; tag_name: string; matched_ruleset: string; confidence: number }[]> {
  const titleText = `${title} ${sourceUrl}`.toLowerCase()
  const fullText = `${title} ${description} ${sourceUrl}`.toLowerCase()
  const matchedTags: { tag_id: string; tag_name: string; matched_ruleset: string; confidence: number }[] = []
  const titleMatchedTagIds = new Set<string>()

  try {
    // Load all active tag rulesets
    const { data: rulesets, error: rulesetsError } = await supabase
      .from('rulesets')
      .select('id, scope, target_id, version, status, display_order, description, created_at, updated_at, published_at, published_by')
      .eq('scope', 'tag')
      .eq('status', 'active')
      .order('display_order', { ascending: true })

    if (rulesetsError) throw rulesetsError
    if (!rulesets || rulesets.length === 0) return []

    // Load tags to get names
    const { data: tags, error: tagsError } = await supabase
      .from('tags')
      .select('id, name')

    if (tagsError) throw tagsError

    // TIER 1: Check title matches first (higher confidence)
    for (const ruleset of rulesets) {
      const tag = tags?.find(t => t.id === ruleset.target_id)
      if (!tag) continue

      let rules: RuleLogic
      try {
        rules = ruleset.description ? JSON.parse(ruleset.description) : {}
      } catch (parseError) {
        console.warn(`⚠️  Failed to parse rules for tag ${tag.name}:`, parseError)
        continue
      }

      const result = evaluateRules(titleText, rules, tag.name)

      if (result.matched) {
        matchedTags.push({
          tag_id: tag.id,
          tag_name: tag.name,
          matched_ruleset: `${tag.name} v${ruleset.version} (title)`,
          confidence: 0.95 // Higher confidence for title matches
        })
        titleMatchedTagIds.add(tag.id)
      }
    }

    // TIER 2: Check full text for tags not yet matched
    for (const ruleset of rulesets) {
      const tag = tags?.find(t => t.id === ruleset.target_id)
      if (!tag || titleMatchedTagIds.has(tag.id)) continue // Skip if already matched in title

      let rules: RuleLogic
      try {
        rules = ruleset.description ? JSON.parse(ruleset.description) : {}
      } catch (parseError) {
        continue
      }

      const result = evaluateRules(fullText, rules, tag.name)

      if (result.matched) {
        matchedTags.push({
          tag_id: tag.id,
          tag_name: tag.name,
          matched_ruleset: `${tag.name} v${ruleset.version}`,
          confidence: 0.8 // Lower confidence for description matches
        })
      }
    }

    return matchedTags
  } catch (error) {
    console.error('❌ Tag classification error:', error)
    return []
  }
}

/**
 * Logs classification attempt to database for debugging
 */
async function logClassification(
  title: string,
  description: string,
  sourceUrl: string,
  result: ClassificationResult | null,
  error: any | null,
  errorStage: string | null,
  durationMs: number,
  context?: { contributionId?: string; userId?: string; brand?: string; isInitialScrape?: boolean }
) {
  try {
    const logEntry = {
      title: title?.substring(0, 500), // Limit length
      description_length: description?.length || 0,
      source_url: sourceUrl?.substring(0, 500),
      category_id: result?.category_id || null,
      category_name: result?.category_name || null,
      subcategory_id: result?.subcategory_id || null,
      subcategory_name: result?.subcategory_name || null,
      matched_ruleset: result?.matched_ruleset || null,
      confidence: result?.confidence || null,
      tag_count: result?.tags?.length || 0,
      error_occurred: !!error,
      error_message: error?.message?.substring(0, 1000) || null,
      error_stage: errorStage,
      duration_ms: durationMs,
      contribution_id: context?.contributionId || null,
      user_id: context?.userId || null,
      is_initial_scrape: context?.isInitialScrape || false,
      debug_info: result?.debug || null
    }

    await supabase.from('classification_logs').insert(logEntry)
  } catch (logError) {
    // Don't throw if logging fails - just log to console
    console.error('Failed to log classification:', logError)
  }
}

/**
 * Complete classification: category/subcategory + tags
 * Flow:
 * 1. Try subcategory classification (determines both category + subcategory)
 * 2. If no subcategory match, try category classification
 * 3. If still no match, assign "Other" category
 * 4. Classify tags (all matching)
 */
export async function classifyItem(
  title: string,
  description: string,
  sourceUrl: string,
  context?: { contributionId?: string; userId?: string; brand?: string; isInitialScrape?: boolean }
): Promise<ClassificationResult> {
  const startTime = Date.now()
  const debug: ClassificationResult['debug'] = []
  let errorStage: string | null = null

  try {
    // Step 1: Try subcategory classification
    const subcategoryResult = await classifyItemBySubcategory(title, description, sourceUrl).catch(err => {
      errorStage = 'subcategory_classification'
      throw err
    })

    let categoryId = subcategoryResult.category_id
    let categoryName = subcategoryResult.category_name
    let subcategoryId = subcategoryResult.subcategory_id
    let subcategoryName = subcategoryResult.subcategory_name
    let matchedRuleset = subcategoryResult.matched_ruleset
    let confidence = subcategoryResult.confidence

    // Merge debug info
    debug.push(...subcategoryResult.debug)

    // Step 2: If no subcategory match, try category classification
    if (!subcategoryId) {
      const itemText = `${title} ${description} ${sourceUrl}`.toLowerCase()
      const categoryResult = await classifyItemByCategory(itemText, debug).catch(err => {
        errorStage = 'category_classification'
        throw err
      })

      if (categoryResult) {
        categoryId = categoryResult.category_id
        categoryName = categoryResult.category_name
        matchedRuleset = categoryResult.matched_ruleset
        confidence = 'medium'
      }
    }

    // Step 3: Classify tags
    const tags = await classifyItemByTags(title, description, sourceUrl).catch(err => {
      errorStage = 'tag_classification'
      // Don't throw - tags are optional, continue with empty tags
      return []
    })

    const result: ClassificationResult = {
      category_id: categoryId,
      category_name: categoryName,
      subcategory_id: subcategoryId,
      subcategory_name: subcategoryName,
      matched_ruleset: matchedRuleset,
      confidence: confidence,
      tags: tags,
      debug: debug
    }

    // Log successful classification
    const durationMs = Date.now() - startTime
    await logClassification(title, description, sourceUrl, result, null, null, durationMs, context)

    return result
  } catch (error) {
    // Log failed classification
    const durationMs = Date.now() - startTime
    await logClassification(title, description, sourceUrl, null, error, errorStage, durationMs, context)

    // Re-throw the error so calling code can handle it
    throw error
  }
}
